import { useState } from "react";
import { useAuth, API, safeStorage } from "../App";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Lock, IdCard, LogIn, HelpCircle, Building2, ShieldCheck, Play } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { BRAND } from "../lib/branding";
import { useForceLightMode } from "../hooks/useForceLightMode";

const LoginPage = () => {
  useForceLightMode();
  const [lovskaIzkaznica, setLovskaIzkaznica] = useState("");
  const [stevilkaClana, setStevilkaClana] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [ldOptions, setLdOptions] = useState(null); // dual membership choice
  const { login } = useAuth();
  const navigate = useNavigate();

  const startDemo = async (role) => {
    setIsLoading(true);
    try {
      const r = await axios.post(`${API}/demo/login`, { role });
      const { token: newToken, user: userData } = r.data;
      safeStorage.setItem("token", newToken);
      if (userData?.ld_id) safeStorage.setItem("ld_id", userData.ld_id);
      if (userData?.ld_naziv) safeStorage.setItem("ld_naziv", userData.ld_naziv);
      await login("", "", null, { skipRequest: true, prefetched: { token: newToken, user: userData } });
      toast.success(`Demo vstop kot ${role === "predsednik" ? "predsednik" : "član"}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka pri demo vstopu");
    } finally { setIsLoading(false); }
  };

  const tryLogin = async (ldId = null) => {
    setIsLoading(true);
    try {
      const payload = { lovska_izkaznica: lovskaIzkaznica, stevilka_clana: stevilkaClana };
      if (ldId) payload.ld_id = ldId;
      const r = await axios.post(`${API}/auth/login`, payload);
      // Multi-LD choice needed?
      if (r.data.needs_ld_choice) {
        const opts = r.data.options || [];
        if (opts.length === 0) {
          toast.error("Račun ni pravilno konfiguriran (0 aktivnih LD). Obrnite se na administratorja.");
          setIsLoading(false);
          return;
        }
        if (opts.length === 1) {
          // Only one option — pick it automatically, no need to prompt.
          setIsLoading(false);
          tryLogin(opts[0].ld_id);
          return;
        }
        setLdOptions(opts);
        setIsLoading(false);
        return;
      }
      // Normal login path: store token + user
      const { token: newToken, user: userData } = r.data;
      safeStorage.setItem("token", newToken);
      if (userData?.ld_id) safeStorage.setItem("ld_id", userData.ld_id);
      if (userData?.ld_naziv) safeStorage.setItem("ld_naziv", userData.ld_naziv);
      // Set through context helper by calling a lightweight direct set
      await login(lovskaIzkaznica, stevilkaClana, ldId, { skipRequest: true, prefetched: { token: newToken, user: userData } });
      toast.success(userData?.ld_naziv ? `Prijava v ${userData.ld_naziv}` : "Prijava uspešna!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka pri prijavi");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    tryLogin(null);
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <Card className="glass border-0 shadow-2xl">
          <CardHeader className="text-center space-y-4 pb-2">
            <div className="mx-auto w-28 h-28 rounded-full overflow-hidden shadow-lg bg-white ring-4 ring-primary/20">
              <img src={BRAND.logoPath} alt={BRAND.brandName} className="w-full h-full object-contain" data-testid="login-logo" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-primary" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '0.02em' }}>{BRAND.brandName}</CardTitle>
              <CardDescription className="text-muted-foreground mt-2">{BRAND.tagline}</CardDescription>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="lovska_izkaznica" className="text-sm font-medium">Št. lovske izkaznice</Label>
                  <Dialog open={showHelp} onOpenChange={setShowHelp}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-muted-foreground hover:text-primary" data-testid="login-help-btn">
                        <HelpCircle className="h-4 w-4 mr-1" />Pomoč
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader><DialogTitle>Navodila za prijavo</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Za prijavo uporabi podatke z lovske izkaznice:</p>
                        <div className="border rounded-lg overflow-hidden">
                          <img src="https://customer-assets.emergentagent.com/job_9b095d54-6409-48dc-959a-2cf846dfcc91/artifacts/0472quhb_lovska-izkaznica.png" alt="" className="w-full" />
                        </div>
                        <div className="space-y-2 text-sm">
                          <div><span className="font-bold text-primary">Uporabnik:</span> Št. lovske izkaznice</div>
                          <div><span className="font-bold text-primary">Geslo:</span> Št. člana</div>
                        </div>
                        <p className="text-xs text-muted-foreground">Sistem samodejno prepozna, kateri lovski družini pripadaš. Če si član več LD, ti bo ponujen izbor.</p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="relative">
                  <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="lovska_izkaznica" type="text" placeholder="Vnesi št. izkaznice"
                    value={lovskaIzkaznica} onChange={(e) => setLovskaIzkaznica(e.target.value)}
                    className="pl-10 h-12" required data-testid="login-license-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="stevilka_clana" className="text-sm font-medium">Geslo / Št. člana</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="stevilka_clana" type="password" placeholder="Vnesi št. člana"
                    value={stevilkaClana} onChange={(e) => setStevilkaClana(e.target.value)}
                    className="pl-10 h-12" required data-testid="login-member-input" />
                </div>
              </div>

              <Button type="submit" className="w-full h-12 text-base font-medium" disabled={isLoading} data-testid="login-submit-btn">
                {isLoading ? (<div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />) :
                  (<><LogIn className="mr-2 h-5 w-5" />Prijava</>)}
              </Button>
            </form>

            {BRAND.showLandingPage && (
              <div className="mt-6 pt-4 border-t space-y-3">
                <Link to="/register-ld" className="text-sm text-primary hover:underline block text-center" data-testid="register-ld-link">
                  Registriraj svojo lovsko družino
                </Link>
                {/* Demo dostop */}
                <div className="pt-2 border-t space-y-2">
                  <p className="text-[11px] text-muted-foreground text-center uppercase tracking-wide">Predstavitev / demo</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => startDemo("clan")}
                      disabled={isLoading}
                      data-testid="demo-login-clan"
                      className="text-xs"
                      title="Vstop kot navadni član (bralni pregled)"
                    >
                      <Play className="h-3.5 w-3.5 mr-1" />Demo (član)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => startDemo("predsednik")}
                      disabled={isLoading}
                      data-testid="demo-login-predsednik"
                      className="text-xs"
                      title="Vstop kot predsednik (pregled administracijskih možnosti)"
                    >
                      <Play className="h-3.5 w-3.5 mr-1" />Demo (predsednik)
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center leading-tight">
                    Testni podatki, se avtomatsko resetirajo. Brez povezovanja s pravimi LD.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dual membership LD picker */}
      <Dialog open={ldOptions !== null} onOpenChange={(o) => { if (!o) setLdOptions(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Izberi lovsko družino
            </DialogTitle>
            <DialogDescription>
              Si član več lovskih družin. V katero se želiš prijaviti?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            {(ldOptions || []).map(opt => (
              <Button key={opt.ld_id} variant="outline" className="w-full justify-start h-auto py-3"
                onClick={() => { setLdOptions(null); tryLogin(opt.ld_id); }}
                data-testid={`choose-ld-${opt.ld_id}`}>
                <Building2 className="h-4 w-4 mr-2 text-primary flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold">{opt.ld_naziv}</div>
                  <div className="text-xs text-muted-foreground">Vloga: {opt.vloga}</div>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
